import { NumberInput, type NumberInputProps } from "@mantine/core";
import { useEffect } from "react";

import { useCallbacksFormStore } from "../../../callbacksForm.store";
import {
    DEFAULT_CALLBACK_NOW_DELAY,
    MAX_CALLBACK_NOW_DELAY,
    MIN_CALLBACK_NOW_DELAY,
    MIN_CALLBACK_STEP,
} from "../../../types";
import classes from "./inputs.module.css";

interface SecondsInputProps {
    disabled?: boolean;
    id?: string;
    /** Appended to the numeric value, for example `" seconds"`. */
    suffix?: string;
    variant?: NumberInputProps["variant"];
    /** Full-width filled field used by the dynamic callback form. */
    fullWidth?: boolean;
    classNames?: NumberInputProps["classNames"];
}

export function SecondsInput({ disabled, id, suffix, variant, fullWidth = false, classNames }: SecondsInputProps) {
    const selectedCallbackNowDelay = useCallbacksFormStore((state) => state.selectedCallbackNowDelay);
    const { setSelectedCallbackNowDelay } = useCallbacksFormStore.getState();

    function handleChange(value: string | number) {
        if (Number.isNaN(value)) {
            return;
        }
        const newValue = Number(value);
        // this if is a workaround for the step value going to 6-11-16 instead of 5-10-15 when you hit the lower bound and go back up
        if (
            (selectedCallbackNowDelay === MIN_CALLBACK_NOW_DELAY &&
                newValue === MIN_CALLBACK_STEP + MIN_CALLBACK_NOW_DELAY) ||
            (selectedCallbackNowDelay === MAX_CALLBACK_NOW_DELAY &&
                newValue === MAX_CALLBACK_NOW_DELAY - MIN_CALLBACK_STEP)
        ) {
            // using modulus here supports changing the min, max and step values.
            setSelectedCallbackNowDelay(newValue - (newValue % MIN_CALLBACK_STEP));
        } else {
            setSelectedCallbackNowDelay(newValue);
        }
    }

    function setDefaultWhenEmpty() {
        if (!selectedCallbackNowDelay) {
            setSelectedCallbackNowDelay(DEFAULT_CALLBACK_NOW_DELAY);
        }
    }

    // oxlint-disable-next-line exhaustive-deps
    useEffect(setDefaultWhenEmpty, []);

    return (
        <NumberInput
            id={id}
            clampBehavior="blur"
            data-testid="call-controls-callback-delay"
            disabled={disabled}
            onBlur={setDefaultWhenEmpty}
            stepHoldDelay={100}
            step={MIN_CALLBACK_STEP}
            max={MAX_CALLBACK_NOW_DELAY}
            min={MIN_CALLBACK_NOW_DELAY}
            onChange={handleChange}
            allowDecimal={fullWidth ? false : true}
            allowNegative={fullWidth ? false : undefined}
            fz={fullWidth ? undefined : "xs"}
            size={fullWidth ? "sm" : "xs"}
            value={selectedCallbackNowDelay || undefined}
            className={fullWidth ? classes.delay : classes.seconds}
            classNames={classNames}
            suffix={suffix}
            variant={variant}
        />
    );
}
